/**
 * Internal agent wire instruction for the Create-with-Heidi routine-setup chat.
 * The hardcoded first turn already explains routines; the model should interview
 * and propose rather than re-introduce itself.
 */
export const CREATE_ROUTINE_WITH_HEIDI_WIRE_PROMPT =
  'Help me create a Heidi routine from this request. Ask only for missing details one question at a time, then propose a clear routine (name, when it runs, and instructions) for me to review before creating it. Do not explain how routines work unless I ask.';

const USER_REQUEST_MARKER = '\nUser request:\n';

export function buildCreateRoutineWithHeidiWirePrompt({
  description,
  openingMessage,
  seedInstructions,
}: {
  description: string;
  openingMessage: string;
  /**
   * Extra agent-only instruction inserted BEFORE the `User request:` tail —
   * for a seeded turn (e.g. the "Suggest something" button) that needs the
   * agent to do more than the plain wire prompt says, while keeping
   * `description` as the short clinician-facing text
   * `displayBodyFromCreateRoutineWirePrompt` recovers. Never put anything
   * here that should show up in the bubble — only `description` does.
   */
  seedInstructions?: string;
}): string {
  const trimmedDescription = description.trim();
  const trimmedOpening = openingMessage.trim();
  const lines = [CREATE_ROUTINE_WITH_HEIDI_WIRE_PROMPT];
  if (trimmedOpening) {
    lines.push(
      `You already opened this conversation with the message below — the user is reading it directly above their reply:\n"""\n${trimmedOpening}\n"""`,
      "Treat their message as a follow-up in that conversation. Don't re-introduce routines or greet from scratch."
    );
  }
  const trimmedSeedInstructions = seedInstructions?.trim();
  if (trimmedSeedInstructions) {
    lines.push(trimmedSeedInstructions);
  }
  lines.push(`User request:\n${trimmedDescription}`);
  return lines.join('\n\n');
}

/**
 * Render-only: the transcript stores the full wire prompt as the user
 * message. Return the clinician-facing request, or null when this is
 * not a create-routine seed.
 */
export function displayBodyFromCreateRoutineWirePrompt(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed.startsWith(CREATE_ROUTINE_WITH_HEIDI_WIRE_PROMPT)) return null;
  const idx = trimmed.lastIndexOf(USER_REQUEST_MARKER);
  if (idx === -1) return null;
  return trimmed.slice(idx + USER_REQUEST_MARKER.length).trim();
}
